import {
    ClientRect,
    Collision,
    CollisionDescriptor,
    CollisionDetection,
    DroppableContainer,
    UniqueIdentifier,
    closestCenter,
    pointerWithin,
} from "@dnd-kit/core";

import { LomdaV3Block, LomdaV3BlockType } from "@/lib/utils/lomdaV3/types";
import { BlockList, sameBlockList } from "./types";

// Drag kinds carried on each draggable's / droppable's `data.current`.
// `depth` = how many slots wrap it (0 = the page itself).
export type DragData =
    | {
          kind: "palette";
          label: string;
          type: LomdaV3BlockType;
          create: () => LomdaV3Block;
          // A library icon: it drops only onto an icon slot.
          icon?: string;
      }
    | {
          kind: "block";
          label: string;
          type: LomdaV3BlockType;
          list: BlockList;
          index: number;
          depth: number;
      }
    | { kind: "page" }
    | { kind: "page-area" }
    | { kind: "icon-slot"; set: (icon: string) => void }
    | { kind: "slot"; containerId: string; slotId: string; depth: number }
    | {
          kind: "slot-header";
          containerId: string;
          slotId: string;
          count: number;
          depth: number;
      };

export type ActiveDrag = { id: string; data: DragData };

/** Where a drop lands: a list and the spot in it, counted as rendered now. */
export interface DropTarget {
    list: BlockList;
    index: number;
}

export const PAGE_AREA_ID = "lomda-page-area";

/** How far outside the page column the pointer still counts as "on the page".
 *  Same on both sides, so it covers the grip rail in RTL and LTR alike. */
const SIDE_MARGIN = 64;

type CollisionArgs = Parameters<CollisionDetection>[0];

const dataOf = (c: DroppableContainer) => c.data.current as DragData | undefined;

const midY = (r: ClientRect) => r.top + r.height / 2;

// Innermost wins: a nested block sits in a slot, which sits in its container.
const rank = (d: DragData | undefined): number => {
    if (d?.kind === "block") return d.depth * 2 + 1;
    if (d?.kind === "slot" || d?.kind === "slot-header") return d.depth * 2;
    if (d?.kind === "page-area") return 0;
    return -1;
};

/** The spot in `list` at height `y`: how many of its blocks sit above it. */
const indexAt = (list: BlockList, y: number, args: CollisionArgs) =>
    args.droppableContainers.filter((c) => {
        const d = dataOf(c);
        const r = args.droppableRects.get(c.id);
        return (
            d?.kind === "block" && sameBlockList(d.list, list) && !!r && midY(r) < y
        );
    }).length;

/** Sidebar page reorder: only pages are targets. */
export const pageCollisions: CollisionDetection = (args) => {
    const within = pointerWithin(args);
    const base = within.length > 0 ? within : closestCenter(args);
    return base.filter((c) => {
        const container = args.droppableContainers.find((d) => d.id === c.id);
        return container && dataOf(container)?.kind === "page";
    });
};

const targetUnderPointer = (
    args: CollisionArgs,
    drag: ActiveDrag,
    hits: DroppableContainer[],
    pointer: { x: number; y: number }
): { id: UniqueIdentifier; target: DropTarget } | null => {
    const best = hits
        .filter((c) => rank(dataOf(c)) > 0)
        .sort((a, b) => rank(dataOf(b)) - rank(dataOf(a)))[0];
    const d = best && dataOf(best);

    if (d?.kind === "block") {
        if (best.id === drag.id) return null;
        const rect = args.droppableRects.get(best.id);
        const after = !!rect && pointer.y > midY(rect);
        return {
            id: best.id,
            target: { list: d.list, index: d.index + (after ? 1 : 0) },
        };
    }
    if (d?.kind === "slot") {
        const list = { containerId: d.containerId, slotId: d.slotId };
        return { id: best.id, target: { list, index: indexAt(list, pointer.y, args) } };
    }
    if (d?.kind === "slot-header") {
        const list = { containerId: d.containerId, slotId: d.slotId };
        return { id: best.id, target: { list, index: d.count } };
    }

    // On the page between blocks, or in the side margin beside it.
    const rect = args.droppableRects.get(PAGE_AREA_ID);
    if (
        !rect ||
        pointer.x < rect.left - SIDE_MARGIN ||
        pointer.x > rect.right + SIDE_MARGIN
    )
        return null;
    return {
        id: PAGE_AREA_ID,
        target: { list: null, index: indexAt(null, pointer.y, args) },
    };
};

/**
 * Block / palette drags. Picks the innermost target under the pointer and turns
 * it into a `DropTarget`, carried on the collision's `data.target`. A target
 * `canDrop` refuses, or a drop that changes nothing, returns no collision — so
 * nothing is highlighted and the drop does nothing.
 */
export function blockCollisions(
    args: CollisionArgs,
    drag: ActiveDrag,
    canDrop: (drag: ActiveDrag, list: BlockList) => boolean
): Collision[] {
    const pointer = args.pointerCoordinates;
    if (!pointer) return [];

    const hits = (pointerWithin(args) as CollisionDescriptor[]).map(
        (c) => c.data.droppableContainer
    );

    // An icon only sets a flip-card or tab icon; it never adds a block.
    if (drag.data.kind === "palette" && drag.data.icon) {
        const slot = hits.find((c) => dataOf(c)?.kind === "icon-slot");
        return slot ? [{ id: slot.id }] : [];
    }

    // A top-level block dropped on a page in the sidebar moves to that page.
    const page = hits.find((c) => dataOf(c)?.kind === "page");
    if (page) {
        const topLevel = drag.data.kind === "block" && drag.data.list === null;
        return topLevel ? [{ id: page.id }] : [];
    }

    const hit = targetUnderPointer(args, drag, hits, pointer);
    if (!hit || !canDrop(drag, hit.target.list)) return [];
    const from = drag.data;
    if (
        from.kind === "block" &&
        sameBlockList(from.list, hit.target.list) &&
        (hit.target.index === from.index || hit.target.index === from.index + 1)
    )
        return [];
    return [{ id: hit.id, data: { target: hit.target } }];
}

export const readDropTarget = (
    collisions: Collision[] | null | undefined
): DropTarget | null =>
    (collisions?.[0]?.data?.target as DropTarget | undefined) ?? null;
